import React, { memo } from 'react'

import clsx from 'clsx'

/*
 NOTE:
 スタイルガイドのText周りの使用用途に合わせて変更する。
 とりあえず良く使われそうな 10 ~ 22px までを定義
 スタイルガイド: https://www.figma.com/file/hGHUvL8W2dlsVWcl3mgMKg/%F0%9F%8D%ADROOV-Styleguide-2019~?node-id=105%3A685
 */
export type FontSizeType = 'xs' | 's' | 'm' | 'l'
type LineHeightType = 's' | 'm' | 'l'
export type LetterType = 0 | 5 | 10
type FontType = 'main' | 'alphabet'

// 受け取れるタグの種類
type AllowedTags = 'div' | 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'

type ElementType<T extends AllowedTags> = T extends 'div'
  ? HTMLDivElement
  : T extends 'span'
  ? HTMLSpanElement
  : T extends 'p'
  ? HTMLParagraphElement
  : T extends 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  ? HTMLHeadingElement
  : HTMLElement

type Props<T extends AllowedTags = 'div'> = {
  children: React.ReactNode
  tag?: T
  sizeType?: FontSizeType
  letterType?: LetterType
  fontType?: FontType
  lineHeight?: LineHeightType
  forwardRef?: React.Ref<ElementType<T>>
  className?: string
}

// eslint-disable-next-line prefer-arrow-callback
const BaseTxt = memo(function BaseTxt<T extends AllowedTags>({
  tag = 'div' as T,
  children,
  sizeType = 'm',
  letterType = 0,
  fontType = 'main',
  lineHeight = 'm',
  forwardRef,
  className,
}: Props<T>) {
  const TagName = tag as React.ElementType

  return (
    // 基本のタグは as で上書きする
    <TagName
      ref={forwardRef}
      className={clsx(
        sizeType === 'xs'
          ? 'tw-text-s-2'
          : sizeType === 's'
          ? 'tw-text-s-1'
          : sizeType === 'm'
          ? 'tw-text-s0'
          : 'tw-text-s1',

        letterType === 0
          ? 'tw-tracking-[0.018em]'
          : letterType === 5
          ? 'tw-tracking-[0.05em]'
          : 'tw-tracking-[0.1em]',

        fontType === 'alphabet' && 'tw-font-alphabet',

        lineHeight === 's'
          ? 'tw-leading-[1]'
          : lineHeight === 'm'
          ? 'tw-leading-[1.4]'
          : 'tw-leading-[1.8]',

        className
      )}
    >
      {children}
    </TagName>
  )
})

// eslint-disable-next-line prefer-arrow-callback
export const Txt = React.forwardRef(function Txt<T extends AllowedTags>(
  props: Props<T>,
  ref: React.Ref<ElementType<T>>
) {
  return <BaseTxt {...props} forwardRef={ref} />
})
